import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { fetchApiPost } from "@/lib/api/utils";
import { KnowledgeApi, useAddAssignment, useGetAssignableUsers, useDeleteTask } from "@/lib/api/knowledge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GenerateLoadingOrError } from "@/lib/utils";
import { Combobox } from "@/components/ui/combobox";
import { useNavigate } from "react-router-dom";

interface DeleteTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string;
}


export function DeleteTaskDialog({
  open,
  onOpenChange,
  taskId
}: DeleteTaskDialogProps) {
    const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assigneeId, setAssigneeId] = useState("");
  const [trainerId, setTrainerId] = useState("");
  const [backupTrainerId, setBackupTrainerId] = useState("");
  const deleteTask = useDeleteTask();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!taskId) {
      toast({
        title: "Error",
        description: "",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await deleteTask({
        id: taskId
      });

      if (!result.success) {
        toast({
          title: "Failed to delete task",
          description: "Please try again.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Deleted",
        description: "The task has been assigned successfully.",
      });

      // Close dialog and reset form
      onOpenChange(false);
      navigate("/tasks");
    } catch (error) {
      console.error("Error deleting task:", error);
      toast({
        title: "Error",
        description: "Failed to delete task. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Delete Task</DialogTitle>
            <DialogDescription>
              Delete this task. Only the owner can delete a task.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}